import { Spin, notification } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageTitle from 'components/page-title';
import TabbedPage from 'components/tabbed-page';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import NoPermission from './components/no-permission';
import { DataImportContext, DataImportContextValue } from './context';
import DailyTab from './tabs/daily-tab';
import HistoryTab from './tabs/history-tab';
import MonthlyTab from './tabs/monthly-tab';
import PermissionsTab from './tabs/permissions-tab';
import { importErrorMessage, isNotAllowed, isStudioBlocked, studioBlockedMessage } from './utils';
import styles from './style.module.scss';

const TITLE = 'Nhập dữ liệu';

type Probe = 'idle' | 'allowed' | 'denied';

/**
 * Doanh thu → Nhập dữ liệu (docs/nhap-du-lieu/DAC-TA-2026-09-29.md, mục 9.2): đưa file
 * "Dữ liệu nhà sáng tạo" của TikTok LIVE Backstage vào hệ thống theo ngày và theo tháng.
 * Admin luôn được nhập; MOD chỉ được khi Admin bật ở tab "Người được nhập".
 * Backend từ chối quyền bằng 409 DATA_IMPORT_NOT_ALLOWED (không 401/403).
 */
const DataImportPage = () => {
  const { loaded, failed, isAdmin, canImportData } = useCurrentRoles();
  // Backend trả 409 DATA_IMPORT_NOT_ALLOWED giữa chừng (Admin vừa thu quyền).
  const [denied, setDenied] = useState(false);
  // Cổng Studio trả 409 STUDIO_ROUTE_BLOCKED (Quản lý Studio, kể cả khi đã được cấp quyền nhập):
  // cùng khối "không được nhập" nhưng hiện đúng câu của máy chủ. null = câu mặc định.
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [probe, setProbe] = useState<Probe>('idle');

  const allowedByRole = isAdmin || !!canImportData;
  // useCurrentRoles nhớ kết quả theo phiên đăng nhập. Trước khi hiện khối "chưa được cấp
  // quyền" hỏi lại /data-import/access đúng một lần (luôn 200, không phải API nhập), để MOD
  // vừa được Admin cấp quyền vào được trang mà không phải tải lại.
  const needProbe = loaded && !failed && !allowedByRole;

  useEffect(() => {
    if (!needProbe) return undefined;
    let cancelled = false;
    AxiosUtils.get(API.dataImportAccess)
      .then((res: any) => {
        if (!cancelled) setProbe(res?.data?.canImport ? 'allowed' : 'denied');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isStudioBlocked(err)) setBlockedMessage(studioBlockedMessage(err));
        setProbe('denied');
      });
    return () => {
      cancelled = true;
    };
  }, [needProbe]);

  const reportError = useCallback((err: unknown, title: string) => {
    if (isStudioBlocked(err)) {
      setBlockedMessage(studioBlockedMessage(err));
      setDenied(true);
      return;
    }
    if (isNotAllowed(err)) {
      setDenied(true);
      return;
    }
    notification.error({ message: title, description: importErrorMessage(err), duration: 6 });
  }, []);

  const ctx: DataImportContextValue = useMemo(
    () => ({ isAdmin, reportError }),
    [isAdmin, reportError]
  );

  if (denied || (needProbe && probe === 'denied')) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <NoPermission message={blockedMessage} />
      </div>
    );
  }

  if (!loaded || (needProbe && probe === 'idle')) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <div className={styles.centered}>
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <DataImportContext.Provider value={ctx}>
      <TabbedPage
        title={TITLE}
        tabs={[
          { key: 'ngay', label: 'Data ngày', render: () => <DailyTab /> },
          { key: 'thang', label: 'Data tháng', render: () => <MonthlyTab /> },
          { key: 'lich-su', label: 'Lịch sử nhập', render: () => <HistoryTab /> },
          {
            key: 'quyen',
            label: 'Người được nhập',
            render: () => <PermissionsTab />,
            hidden: !isAdmin,
          },
        ]}
      />
    </DataImportContext.Provider>
  );
};

export default DataImportPage;
